<script setup>
	import { ref, onMounted } from 'vue';
	import { DsText, DsIconButton } from '@coloplast/design-system';
	import { Swiper, SwiperSlide } from 'swiper/vue/swiper-vue';
	import { Navigation, Keyboard, A11y } from 'swiper';
	import 'swiper/scss';
	import 'swiper/scss/navigation';

	const props = defineProps({
		galleryItem: {
			type: Object,
			default: () => {},
		},
		isForZoom: {
			type: Boolean,
			default: false,
		},
	});

	const activeGalleryItem = ref(props.galleryItem);

	const settings = ref({
		speed: 500,
		keyboard: true,
		navigation: {
			prevEl: '.prev-button-gallery',
			nextEl: '.next-button-gallery',
		},
		modules: [Navigation, Keyboard, A11y],
	});

	const emit = defineEmits(['hidden-modal']);

	const hideModal = () => {
		emit('hidden-modal');
	};

	const modalMask = ref(null);

	onMounted(() => {
		modalMask.value.focus();
	});
</script>

<template>
	<div
		ref="modalMask"
		class="modal-gallery__mask"
		@click.self="hideModal"
		@keyup.esc="hideModal"
	>
		<div
			class="modal-gallery__wrapper"
			:class="{ 'modal-gallery__wrapper--zoom' : isForZoom }"
		>
			<DsIconButton
				type="button"
				aria-label="Close"
				icon-name="close"
				:variant="!isForZoom ? 'tertiary' : 'ghost-neutral'"
				class="modal-gallery__close"
				:class="{ 'modal-gallery__close--zoom' : isForZoom }"
				:click-handler="hideModal"
			>
			</DsIconButton>
			<DsText
				v-if="galleryItem.Headline && !isForZoom"
				html-element="h3"
				text-type="heading-3xl"
				class="modal-gallery__headline"
			>
				{{ galleryItem.Headline }}
			</DsText>
			<div
				class="modal-gallery__container"
				:class="{ 'modal-gallery__container--zoom' : isForZoom }"
			>
				<Swiper
					v-if="activeGalleryItem?.Images?.length > 1"
					v-bind="settings"
				>
					<SwiperSlide
						v-for="(slide, index) in activeGalleryItem.Images"
						:key="`${galleryItem.Headline}-${index}`"
					>
						<div class="modal-gallery__slide">
							<div
								class="modal-gallery__image"
								:class="{ 'modal-gallery__image--zoom' : isForZoom }"
							>
								<picture>
									<source
										media="(max-width: 767px)"
										:srcset="`${slide.ImageUrl}?im=Resize=(280,280)`"
									/>
									<source
										v-if="isForZoom"
										media="(min-width: 767px)"
										:srcset="`${slide.ImageUrl}?im=Resize=(1192,670)`"
									/>
									<img
										:src="`${slide.ImageUrl}?im=Resize=(696,400)`"
										:alt="galleryItem.Headline"
									/>
								</picture>
							</div>
						</div>
					</SwiperSlide>
				</Swiper>
				<div
					v-else
					class="swiper"
				>
					<div class="modal-gallery__slide">
						<div
							class="modal-gallery__image"
							:class="{ 'modal-gallery__image--zoom' : isForZoom }"
						>
							<picture>
								<source
									media="(max-width: 767px)"
									:srcset="`${activeGalleryItem.Images[0].ImageUrl}?im=Resize=(280,280)`"
								/>
								<source
									v-if="isForZoom"
									media="(min-width: 767px)"
									:srcset="`${activeGalleryItem.Images[0].ImageUrl}?im=Resize=(1192,670)`"
								/>
								<img
									:src="`${activeGalleryItem.Images[0].ImageUrl}?im=Resize=(696,400)`"
									:alt="galleryItem.Headline"
								/>
							</picture>
						</div>
					</div>
				</div>
				<nav
					v-if="activeGalleryItem?.Images?.length > 1"
					class="modal-gallery__navigation"
				>
					<DsIconButton
						aria-label="Previous"
						icon-name="chevron-left"
						type="button"
						class="prev-button-gallery"
					>
					</DsIconButton>
					<DsIconButton
						aria-label="Next"
						icon-name="chevron-right"
						type="button"
						class="next-button-gallery"
					>
					</DsIconButton>
				</nav>
			</div>
			<DsText
				v-if="galleryItem?.Attributes?.length"
				text-type="body-sm"
				class="modal-gallery__description"
			>
				<span
					v-for="(attribute, index) in galleryItem.Attributes"
					:key="index"
				>
					{{ attribute.Name + ' ' + attribute.Value + '; ' }}
				</span>
			</DsText>
		</div>
	</div>
</template>

<style lang="scss" scoped>
	@import 'multisite/styles/settings/settings';
	@import 'multisite/styles/tools/tools';

	$hcpContainerSmall: 840px;
	$hcpContainerMedium: 1056px;

	.modal-gallery {
		&__mask {
			display: flex;
			justify-content: center;
			align-items: center;
			position: fixed;
			top: 0;
			bottom: 0;
			left: 0;
			right: 0;
			background-color: rgba(ds-color-raw("bg", "backdrop"), 0.80);
			z-index: 1001;
		}

		&__wrapper {
			max-width: $hcpContainerSmall;
			width: 100%;
			min-height: 100%;
			max-height: 100%;
			padding: ds-space(11) ds-space(8);
			border-radius: 8px;
			overflow: hidden;
			outline: 0 none;
			background-color: ds-color("bg", "default");
			box-shadow: 0 20px 24px -4px rgba(21, 39, 45, 0.12), 0px 8px 12px -4px rgba(21, 39, 45, 0.08);
			position: relative;

			@media (min-width: ds-breakpoint("md")) {
				min-height: initial;
				padding: ds-space(14);
			}

			&--zoom {
				@media (min-width: ds-breakpoint("md")) {
					width: fit-content;
					max-width: ds-container-max("2xl");
					height: fit-content;
					max-height: 830px;
					padding: 80px ds-space(10);
				}
			}
		}

		&__headline {
			max-width: 256px;
			margin-bottom: ds-space(6);

			@media (min-width: ds-breakpoint("md")) {
				max-width: 100%;
				margin-bottom: ds-space(8);
			}
		}

		&__description {
			max-width: 279px;
			text-align: right;
			margin-top: ds-space(6);
			margin-left: auto;
			color: ds-color("fg", "muted");

			@media (min-width: ds-breakpoint("md")) {
				max-width: 100%;
			}
		}

		&__container {
			display: flex;
			align-items: center;
			justify-content: center;
			height: 100%;
			width: 100%;
			position: relative;

			.swiper {
				width: 100% !important;
			}

			&--zoom {
				height: fit-content;
				width: fit-content;

				.swiper {
					width: initial !important;
					border-radius: ds-border-radius("md");
				}
			}
		}

		&__navigation {
			position: absolute;
			bottom: 10px;
			right: 10px;
			z-index: 1;

			@media (min-width: ds-breakpoint("md")) {
				bottom: 16px;
				right: 16px;
			}

			& > button:last-of-type {
				margin-left: ds-space(6);
			}
		}

		&__slide {
			display: flex;
			justify-content: center;
			align-items: center;
			height: 100%;
		}

		&__image {
			display: flex;
			justify-content: center;
			align-items: center;
			background: ds-color("bg", "decorative-1-subtle");
			width: 100%;
			max-width: 696px;
			height: 280px;
			border-radius: ds-border-radius("md");

			@media (min-width: ds-breakpoint("md")) {
				height: 400px;
			}

			img {
				width: 100%;
				height: 280px;
				object-fit: contain;
				user-select: none;

				@media (min-width: ds-breakpoint("md")) {
					height: 400px;
				}
			}

			&--zoom, &--zoom img {
				@media (min-width: ds-breakpoint("md")) {
					width: 100%;
					max-width: 1192px;
					height: 100%;
					max-height: 670px;
					border-radius: ds-border-radius("md");
					background: transparent;
				}
			}
		}
	}

	:deep(.modal-gallery__close) {
		position: absolute;
		top: ds-space(5);
		right: ds-space(5);
		z-index: 100;

		@media (min-width: ds-breakpoint("md")) {
			top: ds-space(9);
			right: ds-space(9);
		}
	}

	:deep(.modal-gallery__close--zoom) {
		@media (min-width: ds-breakpoint("md")) {
			top: ds-space(6);
			right: ds-space(6);
		}
	}
</style>
